import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import Button from '../../atoms/button';

import IconCirclePlus from '../../atoms/icon-circle-plus';
import IconCircleMinus from '../../atoms/icon-circle-minus';

const ZoomClose = styled(Button)`
  background: ${(props) => props.theme.color.white};
  border-radius: 100%;
  opacity: 0;
  outline: 0;
  margin: 1rem;
  position: absolute;
  right: 0;
  top: 0;
  
  svg {
    transform: rotate(45deg);
  }
`;

const ZoomControls = styled.div`
  background: ${(props) => props.theme.color.white};
  border-radius: 5rem;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 50%;
  margin: 1rem;
  opacity: 0;
  position: absolute;
  transform: translateX(-50%);
`;

const ZoomInButton = styled(Button)`
  border-radius: 100%;
  outline: 0;
`;

const ZoomOutButton = styled(Button)`
  border-radius: 100%;
  margin-right: 1rem;
  outline: 0;
`;

const ZoomContainer = styled.div`
  display: none;
`;

const ZoomWrapper = styled.div`
  left: 3rem;
  position: absolute;
  top: 0;
  width: calc(100% - 6rem);
  z-index: -1;

  svg {
    fill: transparent;
    stroke-width: 3;
    stroke: ${(props) => props.theme.color.brand};
  }

  img {
    display: block;
    max-width: 100%;
    top: 0;
  }

  ${ZoomClose},
  ${ZoomControls} {
    visibility: hidden;
  }

  ${(props) => props.zoomOpened && css`
    background: ${props.theme.color.white};
    height: 100%;
    left: 0;
    max-height: 100vh;
    position: fixed;
    width: 100%;
    z-index: ${props.theme.layers.box};

    img {
      opacity: 0;
      z-index: ${props.theme.layers.balcony};
    }

    ${ZoomContainer} {
      background-image: url(${props.image});
      background-position: 50% 50%;
      background-repeat: no-repeat;
      background-size: 100%;
      cursor: all-scroll;
      display: block;
      height: 100%;
      object-fit: none;
      object-position: 0;
      overflow: hidden;
      position: relative;
      width: 100%;
    }

    ${ZoomClose},
    ${ZoomControls} {
      opacity: 1;
      z-index: ${props.theme.layers.balcony};
      visibility: visible;
    }
  `}
`;

const calculateZoom = (x) => x * 50 + 100;

class ProductCarouselZoom extends Component {
  constructor(props) {
    super(props);

    this.state = {
      zoom: 0
    };

    this.maxNumberOfZooms = 3;
    this.zoomContainerRef = createRef();

    this.closeZoom = this.closeZoom.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.zoomOut = this.zoomOut.bind(this);
    this.handleOnMouseMove = this.handleOnMouseMove.bind(this);
    this.calculateBackgroundPosition = this.calculateBackgroundPosition.bind(this);
  }

  closeZoom() {
    this.setState({ zoom: 0 }, () => {
      this.zoomContainerRef.style.backgroundSize = `${calculateZoom(this.state.zoom)}%`;
      this.zoomContainerRef.style.backgroundPosition = '50% 50%';
      this.props.onClose();
    });
  }

  zoomIn() {
    this.setState((prevState) => ({
      zoom: prevState.zoom < this.maxNumberOfZooms ? prevState.zoom + 1 : prevState.zoom
    }), () => {
      this.zoomContainerRef.style.backgroundSize = `${calculateZoom(this.state.zoom)}%`;
    });
  }

  zoomOut() {
    this.setState((prevState) => ({
      zoom: prevState.zoom > 0 ? prevState.zoom - 1 : prevState.zoom
    }), () => {
      this.zoomContainerRef.style.backgroundSize = `${calculateZoom(this.state.zoom)}%`;
    });
  }

  calculateBackgroundPosition(pointer) {
    const screenX = pointer.x ? pointer.x : pointer.x;
    const screenY = pointer.y ? pointer.y : pointer.y;

    let x = (screenX / global.window.innerWidth) * 100;
    const y = (screenY / global.window.innerHeight) * 100;

    x = pointer.x ? x : 100 - x;

    this.zoomContainerRef.style.backgroundPosition = `${x}% ${y}%`;
  }

  handleOnMouseMove(e) {
    if (e.screenX) {
      this.calculateBackgroundPosition({ x: e.screenX, y: e.screenY });
    }
  }

  handleOnTouchMove(e) {
    if (e.touches[0]) {
      this.calculateBackgroundPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  }

  render() {
    return (
      <ZoomWrapper
        image={this.props.image}
        zoomAnimating={this.props.zoomAnimating}
        zoomClosed={this.props.zoomClosed}
        zoomOpened={this.props.zoomOpened}
      >
        <ZoomClose
          aria-label="close zoom"
          onClick={this.closeZoom}
          isIcon
          outline
          inverted
        >
          <IconCirclePlus />
        </ZoomClose>

        <ZoomContainer
          ref={(node) => { this.zoomContainerRef = node; }}
          onTouchMove={(e) => this.handleOnTouchMove(e)}
          onMouseMove={(e) => this.handleOnMouseMove(e)}
        >
          <img src={this.props.image} alt={`${this.props.alt} - zoom`} />
        </ZoomContainer>

        <ZoomControls>
          <ZoomOutButton
            aria-label="zoom out product zoom"
            isIcon
            outline
            inverted
            onClick={this.zoomOut}
            disabled={this.state.zoom <= 0}
          >
            <IconCircleMinus />
          </ZoomOutButton>

          <ZoomInButton
            aria-label="zoom in product zoom"
            aria-hidden={this.props.zoomClosed}
            isIcon
            outline
            inverted
            onClick={this.zoomIn}
            disabled={this.state.zoom >= this.maxNumberOfZooms}
          >
            <IconCirclePlus />
          </ZoomInButton>
        </ZoomControls>
      </ZoomWrapper>
    );
  }
}

ProductCarouselZoom.defaultProps = {
  onClose: () => {},
  zoomAnimating: false,
  zoomClosed: false,
  zoomOpened: false
};

ProductCarouselZoom.propTypes = {
  image: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  zoomAnimating: PropTypes.bool,
  zoomClosed: PropTypes.bool,
  zoomOpened: PropTypes.bool
};

export default ProductCarouselZoom;
